/**
 * The email inbox of a business: its three addresses, who may send to them, and the emails that
 * arrived. Emails are received and booked by services/inbox; the browser only reads the result,
 * approves quarantined emails (through the service, which holds the rights) and edits settings.
 */
import { env } from "@/lib/platform/env";
import type { InboundMessagesResponse } from "@/lib/platform/pb-types";
import { pb } from "@/lib/platform/pocketbase";
import type { DocumentRecord } from "./documents";

export type InboxKind = "ER" | "AR" | "KA";
export const INBOX_KINDS: InboxKind[] = ["ER", "AR", "KA"];

export type FileOutcome = {
  name: string;
  size: number;
  sha256: string;
  outcome: "document" | "attached" | "duplicate" | "not_a_receipt" | "skipped" | "pending";
  reason?: string;
  documentId?: string;
  docNumber?: string;
};

export type InboundMessage = InboundMessagesResponse<{ documents?: DocumentRecord[] }>;

/**
 * "NuFoundry FlexCo" -> "nufoundry-flexco": the business's name in its inbox addresses, so they are
 * easy to tell apart in an email program. Only for reading: the token decides where mail lands.
 */
export function inboxSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length <= 32) return slug;
  // Cut at a word, so "alpenblick-design-und-werbung-gmbh..." does not end mid-word.
  const cut = slug.slice(0, 33);
  return (cut.includes("-") ? cut.slice(0, cut.lastIndexOf("-")) : slug.slice(0, 32)).replace(
    /-+$/,
    "",
  );
}

/** ka-nufoundry-flexco-k7f3q2ab@in.prestobooks.at; without a name, ka-k7f3q2ab@... as before. */
export function inboxAddress(
  kind: InboxKind,
  token: string,
  name = "",
  domain = env.inboxDomain,
): string {
  const slug = inboxSlug(name);
  return `${kind.toLowerCase()}-${slug ? `${slug}-` : ""}${token}@${domain}`;
}

/** Same alphabet as the server hook: no 0/o, 1/l look-alikes in something people retype. */
export function newInboxToken(): string {
  const alphabet = "abcdefghijkmnpqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return [...bytes].map((byte) => alphabet[byte % alphabet.length]).join("");
}

/**
 * "Rechnung@Lieferant.AT" -> "rechnung@lieferant.at"; "lieferant.at" or "@lieferant.at" ->
 * "@lieferant.at" (the whole domain). Mirrors services/inbox/src/lib/sender.ts.
 */
export function normalizeAllowlistEntry(entry: string): string | null {
  const value = entry.trim().toLowerCase();
  if (/^[^@\s]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) return value;
  const domain = value.replace(/^@/, "");
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(domain)) return `@${domain}`;
  return null;
}

export function messageFiles(message: Pick<InboundMessage, "files">): FileOutcome[] {
  return Array.isArray(message.files) ? (message.files as FileOutcome[]) : [];
}

export async function listMessages(workspaceId: string): Promise<InboundMessage[]> {
  return pb.collection("inbound_messages").getFullList<InboundMessage>({
    filter: pb.filter("workspace = {:workspaceId}", { workspaceId }),
    sort: "-created",
    expand: "documents",
  });
}

export async function updateAllowlist(workspaceId: string, allowlist: string[]): Promise<void> {
  await pb.collection("workspaces").update(workspaceId, { inboxAllowlist: allowlist });
}

/** A leaked address stops working at once; the new ones are live immediately. */
export async function rotateInboxToken(workspaceId: string): Promise<string> {
  const token = newInboxToken();
  await pb.collection("workspaces").update(workspaceId, { inboxToken: token });
  return token;
}

export async function approveMessage(
  id: string,
  allowSender: boolean,
): Promise<{ status: string }> {
  const res = await fetch(`${env.inboxUrl}/messages/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${pb.authStore.token}` },
    body: JSON.stringify({ allowSender }),
  });
  if (!res.ok) {
    let message = `The email could not be imported (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep the status message
    }
    throw new Error(message);
  }
  return (await res.json()) as { status: string };
}

export async function discardMessage(id: string): Promise<void> {
  await pb.collection("inbound_messages").update(id, { status: "discarded" });
}
